import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  EmailOutlined,
  MaleOutlined,
  FemaleOutlined,
  PersonOutlineOutlined,
  PhoneOutlined,
  RefreshOutlined,
  SearchOutlined,
  VerifiedOutlined,
  CancelOutlined,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import ApiConfig from "../../Config";
import userService from "../../repository/userRepository";

const Users = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const accentColor = isDark ? "#D1B99A" : "#806B52";
  const tableHeaderBg = isDark ? "#211F1C" : "#F6F2ED";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const fetchUsers = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const token = sessionStorage.getItem("newToken");

      const response = await userService.getAllUsers(token);

      const userData =
        response.data?.UserDat ||
        response.data?.users ||
        response.data?.data ||
        [];

      setUsers(Array.isArray(userData) ? userData : []);
    } catch (error) {
      console.error("Error fetching users:", error);

      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch users",
        severity: "error",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    if (!search) {
      return users;
    }

    return users.filter((user) => {
      return (
        String(user.userId || "")
          .toLowerCase()
          .includes(search) ||
        String(user.name || "")
          .toLowerCase()
          .includes(search) ||
        String(user.email || "")
          .toLowerCase()
          .includes(search) ||
        String(user.contact || "")
          .toLowerCase()
          .includes(search) ||
        String(user.gender || "")
          .toLowerCase()
          .includes(search)
      );
    });
  }, [users, searchTerm]);

  const verifiedEmailCount = users.filter(
    (user) => user.isEmailVerified,
  ).length;

  const verifiedMobileCount = users.filter(
    (user) => user.isMobileVerified,
  ).length;

  const getInitials = (name) => {
    if (!name) return "U";

    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  const formatDate = (date) => {
    if (!date) return "N/A";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "N/A";
    }

    return parsedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getGenderIcon = (gender) => {
    const value = String(gender || "").toLowerCase();

    if (value === "male") {
      return (
        <MaleOutlined
          sx={{
            fontSize: 18,
            color: accentColor,
          }}
        />
      );
    }

    if (value === "female") {
      return (
        <FemaleOutlined
          sx={{
            fontSize: 18,
            color: accentColor,
          }}
        />
      );
    }

    return (
      <PersonOutlineOutlined
        sx={{
          fontSize: 18,
          color: "text.secondary",
        }}
      />
    );
  };

  const verificationChip = (verified) => {
    return verified ? (
      <Chip
        icon={<VerifiedOutlined />}
        label="Verified"
        size="small"
        sx={{
          height: 28,
          borderRadius: 1.5,
          fontWeight: 600,
          color: "#2e7d32",
          backgroundColor: "rgba(46,125,50,0.10)",
          "& .MuiChip-icon": {
            color: "#2e7d32",
          },
        }}
      />
    ) : (
      <Chip
        icon={<CancelOutlined />}
        label="Not Verified"
        size="small"
        sx={{
          height: 28,
          borderRadius: 1.5,
          fontWeight: 600,
          color: "text.secondary",
          backgroundColor: "action.hover",
          "& .MuiChip-icon": {
            color: "text.secondary",
          },
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        backgroundColor: "background.default",
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            md: "row",
          },
          alignItems: {
            xs: "stretch",
            md: "center",
          },
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <PersonOutlineOutlined
              sx={{
                fontSize: 30,
                color: accentColor,
              }}
            />

            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: "text.primary",
                letterSpacing: "-0.5px",
              }}
            >
              Users
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              color: "text.secondary",
            }}
          >
            Manage and view registered Hindustan Wardrobe users
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              width: {
                xs: "100%",
                sm: 300,
              },
              height: 44,
              px: 1.5,
              borderRadius: 2,
              backgroundColor: "action.hover",
              border: "1px solid",
              borderColor: "divider",
              transition: "all 0.2s ease",

              "&:focus-within": {
                borderColor: accentColor,
                boxShadow: isDark
                  ? "0 0 0 3px rgba(209,185,154,0.08)"
                  : "0 0 0 3px rgba(128,107,82,0.08)",
              },
            }}
          >
            <SearchOutlined
              sx={{
                mr: 1,
                color: "text.secondary",
              }}
            />

            <InputBase
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                flex: 1,
                color: "text.primary",
                fontSize: 14,
              }}
            />
          </Paper>

          <Tooltip title="Refresh users">
            <span>
              <IconButton
                onClick={() => fetchUsers(true)}
                disabled={loading || refreshing}
                sx={{
                  width: 44,
                  height: 44,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  color: accentColor,

                  "&:hover": {
                    backgroundColor: isDark
                      ? "rgba(209,185,154,0.08)"
                      : "rgba(128,107,82,0.08)",
                  },
                }}
              >
                {refreshing ? (
                  <CircularProgress
                    size={20}
                    sx={{
                      color: accentColor,
                    }}
                  />
                ) : (
                  <RefreshOutlined />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(3, 1fr)",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2.2,
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: accentColor,
                backgroundColor: isDark
                  ? "rgba(209,185,154,0.12)"
                  : "rgba(128,107,82,0.10)",
              }}
            >
              <PersonOutlineOutlined />
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Users
              </Typography>

              <Typography variant="h5" fontWeight={700} color="text.primary">
                {users.length}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.2,
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#2e7d32",
                backgroundColor: "rgba(46,125,50,0.10)",
              }}
            >
              <EmailOutlined />
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Email Verified
              </Typography>

              <Typography variant="h5" fontWeight={700} color="text.primary">
                {verifiedEmailCount}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.2,
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#2e7d32",
                backgroundColor: "rgba(46,125,50,0.10)",
              }}
            >
              <PhoneOutlined />
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Mobile Verified
              </Typography>

              <Typography variant="h5" fontWeight={700} color="text.primary">
                {verifiedMobileCount}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          overflowX: "auto",
        }}
      >
        <Table
          sx={{
            minWidth: 1100,
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: tableHeaderBg,
              }}
            >
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                User
              </TableCell>

              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                User ID
              </TableCell>

              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                Contact
              </TableCell>

              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                Email
              </TableCell>

              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                Gender
              </TableCell>

              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                Email Status
              </TableCell>

              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                Mobile Status
              </TableCell>

              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                Joined
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  sx={{
                    py: 8,
                    textAlign: "center",
                  }}
                >
                  <CircularProgress
                    size={32}
                    sx={{
                      color: accentColor,
                    }}
                  />

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 2,
                      color: "text.secondary",
                    }}
                  >
                    Loading users...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  sx={{
                    py: 8,
                    textAlign: "center",
                  }}
                >
                  <PersonOutlineOutlined
                    sx={{
                      fontSize: 42,
                      color: "text.disabled",
                    }}
                  />

                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{
                      mt: 1,
                      color: "text.primary",
                    }}
                  >
                    No users found
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      color: "text.secondary",
                    }}
                  >
                    Try changing your search
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow
                  key={user._id || user.userId}
                  hover
                  sx={{
                    "&:last-child td, &:last-child th": {
                      border: 0,
                    },
                  }}
                >
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Avatar
                        src={user.avatar}
                        alt={user.name || "User"}
                        sx={{
                          width: 42,
                          height: 42,
                          backgroundColor: isDark ? "#211F1C" : "#F6F2ED",
                          color: accentColor,
                          fontWeight: 700,
                        }}
                      >
                        {getInitials(user.name)}
                      </Avatar>

                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="text.primary"
                        >
                          {user.name || "N/A"}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          {user.user_DOB ? `DOB: ${user.user_DOB}` : "DOB: N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        color: accentColor,
                      }}
                    >
                      {user.userId || "N/A"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <PhoneOutlined
                        sx={{
                          fontSize: 18,
                          color: "text.secondary",
                        }}
                      />

                      <Typography variant="body2" color="text.primary">
                        {user.contact || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <EmailOutlined
                        sx={{
                          fontSize: 18,
                          color: "text.secondary",
                        }}
                      />

                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                          maxWidth: 220,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user.email || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                      }}
                    >
                      {getGenderIcon(user.gender)}

                      <Typography variant="body2" color="text.primary">
                        {user.gender || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    {verificationChip(user.isEmailVerified)}
                  </TableCell>

                  <TableCell>
                    {verificationChip(user.isMobileVerified)}
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(user.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {filteredUsers.length} of {users.length} users
        </Typography>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((prev) => ({
              ...prev,
              open: false,
            }))
          }
          sx={{
            borderRadius: 2,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
